import React, { useState, useEffect } from 'react';
import { supabase, apiFetch } from '../../lib/supabase';
import { F, C, btn, inp } from '../../config/styles';
import { useTenantConfig } from '../../hooks/useTenant';

export function TeamView({ currentUserId }) {
  const { roles: roleDefinitions, territories, tenantId } = useTenantConfig();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(null);

  // Shopify store connections
  const [stores, setStores]               = useState([]);
  const [storesLoading, setStoresLoading] = useState(true);
  const [connectDomain, setConnectDomain] = useState('');
  const [justConnected, setJustConnected] = useState(null);
  const [connectError, setConnectError]   = useState(null);

  // Manual token connection
  const [manualOpen, setManualOpen]       = useState(false);
  const [manualDomain, setManualDomain]   = useState('');
  const [manualName, setManualName]       = useState('');
  const [manualToken, setManualToken]     = useState('');
  const [manualSaving, setManualSaving]   = useState(false);

  useEffect(() => {
    apiFetch('/api/shopify/stores')
      .then(r => r.json())
      .then(d => setStores(d.stores || []))
      .catch(() => {})
      .finally(() => setStoresLoading(false));
    // Check if redirected back after OAuth
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('store_connected');
    const storeError = params.get('store_error');
    if (connected || storeError) {
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (connected) {
      setJustConnected(decodeURIComponent(connected));
      apiFetch('/api/shopify/stores').then(r => r.json()).then(d => setStores(d.stores || []));
    }
    if (storeError) {
      setConnectError(decodeURIComponent(storeError));
    }
  }, []);

  const connectStore = async () => {
    const domain = connectDomain.trim().toLowerCase();
    if (!domain) return;
    const shop = domain.includes('.myshopify.com') ? domain : `${domain}.myshopify.com`;
    // Fetch the authorize URL with the user's JWT so the server can authenticate the request,
    // derive the tenant from the profile, and set the CSRF nonce cookie — then navigate to Shopify.
    try {
      const res = await apiFetch(`/api/shopify/auth?shop=${encodeURIComponent(shop)}`);
      const data = await res.json();
      if (!res.ok || !data.authUrl) throw new Error(data.error || 'Could not start store connection');
      window.location.href = data.authUrl;
    } catch (e) {
      setConnectError(e.message);
    }
  };

  const updateStoreTerritory = async (store_domain, territory) => {
    await apiFetch('/api/shopify/stores', {
      method: 'PATCH', body: JSON.stringify({ store_domain, territory }),
    });
    setStores(prev => prev.map(s => s.store_domain === store_domain ? { ...s, territory } : s));
  };

  const removeStore = async (store_domain) => {
    if (!window.confirm(`Remove ${store_domain}?`)) return;
    await apiFetch('/api/shopify/stores', {
      method: 'DELETE', body: JSON.stringify({ store_domain }),
    });
    setStores(prev => prev.filter(s => s.store_domain !== store_domain));
  };

  const connectManual = async () => {
    const domain = manualDomain.trim().toLowerCase();
    const name   = manualName.trim();
    const token  = manualToken.trim();
    if (!domain || !name || !token) return;
    const shop = domain.includes('.myshopify.com') ? domain : `${domain}.myshopify.com`;
    setManualSaving(true);
    setConnectError(null);
    const res  = await apiFetch('/api/shopify/stores', {
      method: 'POST', body: JSON.stringify({ store_domain: shop, store_name: name, access_token: token }),
    });
    const data = await res.json();
    setManualSaving(false);
    if (!res.ok) { setConnectError(data.error || 'Failed to connect store'); return; }
    setJustConnected(name);
    setManualDomain(''); setManualName(''); setManualToken(''); setManualOpen(false);
    apiFetch('/api/shopify/stores').then(r => r.json()).then(d => setStores(d.stores || []));
  };

  useEffect(() => {
    if (!tenantId) return;
    supabase.from('profiles').select('*').eq('tenant_id', tenantId).order('email').then(({ data }) => {
      setProfiles(data || []);
      setLoading(false);
    });
  }, [tenantId]);

  // Derive effective roles array from a profile row (handles legacy `role` string)
  const profileRoles = (p) => {
    if (Array.isArray(p.roles) && p.roles.length > 0) return p.roles;
    if (p.role && p.role !== 'pending') return [p.role];
    return [];
  };

  const updateRoles = async (id, newRoles) => {
    setSaving(id);
    const legacyRole = newRoles[0] || 'pending';
    // RPC runs as SECURITY DEFINER — needed so a manager can update another user's profile.
    // It sets both `role` and `roles`; a direct client-side `.update()` on profiles can't do
    // this for anyone but the caller themselves (profiles_self_update RLS is id = auth.uid()).
    await supabase.rpc('update_user_role', { user_id: id, new_role: legacyRole, new_roles: newRoles });
    setProfiles(prev => prev.map(p => p.id === id ? { ...p, roles: newRoles, role: legacyRole } : p));
    setSaving(null);
  };

  const toggleRole = (p, roleId) => {
    const current = profileRoles(p);
    if (roleId === 'manager') {
      // Manager is exclusive — toggling on clears dept roles, toggling off removes it
      updateRoles(p.id, current.includes('manager') ? [] : ['manager']);
      return;
    }
    const isManager = current.includes('manager');
    if (isManager) return; // can't add dept roles while manager
    const next = current.includes(roleId)
      ? current.filter(r => r !== roleId)
      : [...current, roleId];
    updateRoles(p.id, next);
  };

  const DEPT_roleDefinitions = roleDefinitions.filter(r => r.id !== 'manager');

  return (
    <div style={{ maxWidth: 760, margin: '40px auto', padding: '0 20px', fontFamily: F.body }}>
      <div style={{ fontSize: 18, fontWeight: 600, color: C.text, marginBottom: 24 }}>Team</div>

      {/* ── Shopify Store Connections ── */}
      <div style={{ fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 4, marginTop: 36 }}>Shopify Stores</div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>Connect Shopify stores to push approved products directly from the Export tab.</div>
      {storesLoading ? (
        <div style={{ fontSize: 13, color: C.muted }}>Loading…</div>
      ) : (
        <>
          {stores.length > 0 && (
            <div style={{ border: `1px solid ${C.e8}`, borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
              {stores.map((s, i) => (
                <div key={s.store_domain} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderTop: i > 0 ? `1px solid ${C.e8}` : 'none', background: C.white }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: C.text }}>{s.store_name}</div>
                    <div style={{ fontSize: 11, fontFamily: F.mono, color: C.muted, marginTop: 2 }}>{s.store_domain}{s.brand ? ` · ${s.brand}` : ''}</div>
                  </div>
                  <select
                    value={s.territory || ''}
                    onChange={e => updateStoreTerritory(s.store_domain, e.target.value)}
                    style={{ fontSize: 12, padding: '4px 8px', border: `1px solid ${C.e8}`, borderRadius: 6, background: C.white, color: s.territory ? C.text : C.ph }}
                    title="Territory: used when pushing to all stores"
                  >
                    <option value="">No territory</option>
                    {territories.filter(t => t.code !== 'STG').map(t => (
                      <option key={t.code} value={t.code}>{t.flag} {t.name}</option>
                    ))}
                  </select>
                  <span style={{ fontSize: 11, color: '#065f46', background: '#d1fae5', padding: '2px 8px', borderRadius: 20 }}>Connected</span>
                  <button onClick={() => removeStore(s.store_domain)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.ph, fontSize: 16, padding: '2px 4px' }}>×</button>
                </div>
              ))}
            </div>
          )}
          {justConnected && (
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#166534' }}>
              ✓ <strong>{justConnected}</strong> connected successfully
            </div>
          )}
          {connectError && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#991b1b' }}>
              Failed to connect store: {connectError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              style={inp({ flex: 1 })}
              placeholder="your-store.myshopify.com"
              value={connectDomain}
              onChange={e => setConnectDomain(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && connectStore()}
            />
            <button style={btn('primary')} onClick={connectStore} disabled={!connectDomain.trim()}>
              Connect via Shopify →
            </button>
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
            You'll be redirected to Shopify to approve access, then returned here automatically.
          </div>

          {/* Manual token entry — for stores outside the Partner org */}
          <div style={{ marginTop: 12 }}>
            <button
              onClick={() => setManualOpen(o => !o)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: C.muted, padding: 0, textDecoration: 'underline' }}
            >
              {manualOpen ? 'Cancel manual entry' : 'Connect using access token instead'}
            </button>
            {manualOpen && (
              <div style={{ marginTop: 10, padding: '14px 16px', border: `1px solid ${C.e8}`, borderRadius: 8, background: C.fa, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 2 }}>
                  Generate a custom app token in your Shopify Admin → Settings → Apps → Develop apps. Grant <strong>read_products</strong>, <strong>write_products</strong>, <strong>read_inventory</strong> scopes.
                </div>
                <input
                  style={inp()}
                  placeholder="your-store.myshopify.com"
                  value={manualDomain}
                  onChange={e => setManualDomain(e.target.value)}
                />
                <input
                  style={inp()}
                  placeholder="Store name (e.g. UK Store)"
                  value={manualName}
                  onChange={e => setManualName(e.target.value)}
                />
                <input
                  style={inp()}
                  placeholder="shpat_xxxxxxxxxxxxxxxxxxxx"
                  value={manualToken}
                  onChange={e => setManualToken(e.target.value)}
                  type="password"
                />
                <button
                  style={btn('primary', { alignSelf: 'flex-start' })}
                  onClick={connectManual}
                  disabled={manualSaving || !manualDomain.trim() || !manualName.trim() || !manualToken.trim()}
                >
                  {manualSaving ? 'Connecting…' : 'Connect store'}
                </button>
              </div>
            )}
          </div>
        </>
      )}

      <div style={{ fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 4, marginTop: 36 }}>Team members</div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 24 }}>Assign roles to team members. Users can hold multiple department roles. Manager is a standalone role with full access.</div>

      {loading ? (
        <div style={{ color: C.muted, fontSize: 13 }}>Loading…</div>
      ) : profiles.length === 0 ? (
        <div style={{ color: C.muted, fontSize: 13 }}>No users yet.</div>
      ) : (
        <div style={{ border: `1px solid ${C.e8}`, borderRadius: 8, overflow: 'hidden' }}>
          {profiles.map((p, i) => {
            const current = profileRoles(p);
            const isManagerRole = current.includes('manager');
            const isSelf = p.id === currentUserId;
            return (
              <div key={p.id} style={{
                padding: '16px 20px', borderBottom: i < profiles.length - 1 ? `1px solid ${C.e8}` : 'none',
                background: C.white,
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: C.text, fontWeight: 500 }}>{p.email}{isSelf && <span style={{ marginLeft: 6, fontSize: 11, color: C.muted }}>(you)</span>}</div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Joined {new Date(p.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                  </div>
                  {saving === p.id && <span style={{ fontSize: 11, color: C.muted, flexShrink: 0, marginTop: 2 }}>Saving…</span>}
                </div>
                {/* Role checkboxes */}
                <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {/* Manager toggle */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                    background: isManagerRole ? '#1a1a1a' : C.fa, color: isManagerRole ? C.white : C.text,
                    border: `1px solid ${isManagerRole ? '#1a1a1a' : C.e8}`, borderRadius: 20,
                    padding: '4px 12px', fontSize: 12, fontWeight: 500, userSelect: 'none',
                  }}>
                    <input type="checkbox" style={{ display: 'none' }}
                      checked={isManagerRole}
                      onChange={() => {
                        if (isSelf && isManagerRole && !window.confirm('Remove your own Build Manager role? You will lose access until another manager reassigns it.')) return;
                        toggleRole(p, 'manager');
                      }}
                    />
                    Build Manager
                  </label>
                  {/* Dept role pills */}
                  {DEPT_roleDefinitions.map(r => {
                    const checked = current.includes(r.id);
                    return (
                      <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 6,
                        cursor: isManagerRole ? 'not-allowed' : 'pointer',
                        background: checked ? '#e0f2fe' : C.fa,
                        color: checked ? '#0369a1' : isManagerRole ? C.ph : C.text,
                        border: `1px solid ${checked ? '#7dd3fc' : C.e8}`, borderRadius: 20,
                        padding: '4px 12px', fontSize: 12, fontWeight: checked ? 500 : 400,
                        opacity: isManagerRole ? 0.45 : 1, userSelect: 'none',
                      }}>
                        <input type="checkbox" style={{ display: 'none' }}
                          checked={checked}
                          disabled={isManagerRole}
                          onChange={() => toggleRole(p, r.id)}
                        />
                        {r.label}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
